import { appRoutes } from '@/constants/routes';
import type { SortString } from '@/constants/types';
import { getRealId } from '@/hooks/use-document-id';
import type { DocumentWithId } from '@/constants/types';
import type { UserSource } from '@flintmint/core-shared/rxdb';

// TODO change to take SortParams
export const getSortModel = (sort?: Record<string, string> | null): SortString | undefined => {
  if (sort) {
    return `${sort?.key}:${sort?.direction}` as SortString;
  }
  return undefined;
};

export const conditionalSpread = (spread: any, condition: boolean) => {
  if (condition) {
    return spread;
  }
  return Array.isArray(spread) ? [] : {};
};

export const getRandomItem = (arr: any[]) => {
  const randomIndex = Math.floor(Math.random() * arr.length);
  return arr[randomIndex];
};

export const formatDate = (dateStr: string | number) => {
  try {
    const date = new Date(dateStr);

    const year = date.getFullYear();
    const currentYear = new Date().getFullYear();
    const options = {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      ...conditionalSpread(
        {
          year: 'numeric',
        },
        year !== currentYear
      ),
      hour12: true,
    };

    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

    return currentYear ? formattedDate.replace(new RegExp(`${year},`, 'g'), `${year}`) : formattedDate;
  } catch {
    // Invalid date input
    return '';
  }
};

export const copyToClipboard = ({ content, textContent }: { content?: string; textContent?: string }): Promise<void> => {
  if (content === undefined) {
    return navigator.clipboard.write([
      new ClipboardItem({
        ['text/plain']: new Blob([textContent ?? ''], { type: 'text/plain' }),
      }),
    ]);
  }

  return navigator.clipboard.write([
    new ClipboardItem({
      ['text/html']: new Blob([content], { type: 'text/html' }),
      ['text/plain']: new Blob([textContent ?? content], { type: 'text/plain' }),
    }),
  ]);
};

export const constraintValue = (value: number | string, min: number, max: number) => {
  return Math.min(Math.max(Number(value), min), max);
};

export interface Item {
  id: string;
  level: number;
  [key: string]: any;
}

export interface TreeNode extends Item {
  children: TreeNode[];
}

export const buildHierarchy = (flatData: Item[]): TreeNode[] => {
  const map = new Map<string, TreeNode>();
  const root: TreeNode[] = [];
  const levelParentMap: Record<number, TreeNode | null> = {};

  flatData.forEach((item) => {
    const node: TreeNode = { ...item, children: [] };
    map.set(item.id, node);

    if (item.level === 1) {
      root.push(node);
      levelParentMap[item.level] = node;
    } else {
      const parent = levelParentMap[item.level - 1] ?? null;
      if (parent) {
        parent.children.push(node);
      } else {
        root.push(node);
      }
    }

    levelParentMap[item.level] = node;
  });

  return root;
};

type ItemWithId = { id: string | number };

export const toggleObject = <T extends ItemWithId>(array: T[] = [], object: T) => {
  return array.some((el) => el.id === object.id) ? array.filter((el) => el.id !== object.id) : [...array, object];
};

export const getNextFakeProgress = (prev: number, fastThreshold = 80, slowThreshold = 95) => {
  if (prev < fastThreshold) {
    return prev + Math.random() * 10;
  }

  if (prev < slowThreshold) {
    return prev + Math.random() * 2;
  }

  return prev;
};

export const isValidUrl = (url: string) => {
  return /^(https?:\/\/)?[a-zA-Z0-9][-a-zA-Z0-9.]*\.[a-zA-Z]{2,}/.test(url.trim());
};

export const parseFileSource = (fileSource: string) => {
  const path = fileSource.split('/') || [];
  return path[path.length - 1];
};

export const parseSource = (userSource?: UserSource) => {
  if (!userSource?.source) {
    return 'User Created';
  }

  if (userSource?.sourceType === 'url') {
    return userSource.source;
  }

  if (userSource.sourceDisplayName) {
    return userSource.sourceDisplayName;
  }

  return parseFileSource(userSource?.source);
};

export const getColumnsCount = (
  containerWidth: number,
  itemMinWidth: number,
  itemMaxWidth: number,
  gap = 8,
  paddingX = 24,
  maxColumns = 4
): number => {
  if (containerWidth <= 0) return 0;

  const availableWidth = containerWidth - paddingX;

  let columns = Math.floor((availableWidth + gap) / (itemMinWidth + gap));
  columns = Math.min(Math.max(columns, 1), maxColumns);

  const itemWidth = (availableWidth - (columns - 1) * gap) / columns;

  while (itemWidth > itemMaxWidth && columns < maxColumns) {
    columns += 1;
  }

  while (itemWidth < itemMinWidth && columns > 1) {
    columns -= 1;
  }

  return columns;
};

export const hexToRGB = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
};

export const getGridInsertionIndex = (params: {
  activeId: string;
  overId: string;
  overContainer: string;
  prev: Record<string, string[]>;
  draggingRect?: {
    width?: number;
    height?: number;
    left?: number;
    top?: number;
    offsetLeft?: number;
    offsetTop?: number;
  };
  overRect?: {
    width: number;
    height: number;
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
}) => {
  const { overId, overContainer, prev, draggingRect, overRect } = params;
  const overItems = prev[overContainer];

  if (overId in prev) {
    return overItems.length;
  }

  const overIndex = overItems.indexOf(overId);
  if (overIndex < 0) return overItems.length;

  if (!overRect || !draggingRect) return overIndex;

  const overCx = overRect.left + overRect.width / 2;
  const overCy = overRect.top + overRect.height / 2;

  const dragLeft = draggingRect.left ?? draggingRect.offsetLeft ?? 0;
  const dragTop = draggingRect.top ?? draggingRect.offsetTop ?? 0;
  const dragCx = dragLeft + (draggingRect.width ?? 0) / 2;
  const dragCy = dragTop + (draggingRect.height ?? 0) / 2;

  const isAfter = dragCy > overCy || (Math.abs(dragCy - overCy) < overRect.height / 2 && dragCx > overCx);

  return isAfter ? overIndex + 1 : overIndex;
};

export const sortByIdOrder = <T extends DocumentWithId>(items: T[], orderedIds: string[]): T[] => {
  const orderMap = new Map(orderedIds.map((id, index) => [id, index]));
  return [...items].sort((a, b) => {
    const indexA = orderMap.get(a.id) ?? Infinity;
    const indexB = orderMap.get(b.id) ?? Infinity;
    return indexA - indexB;
  });
};

export const getSourceTypeLabel = (sourceType?: string | null): string => {
  switch (sourceType) {
    case 'url':
      return 'url';
    case 'application/pdf':
      return 'pdf';
    case 'text/plain':
      return 'txt';
    case 'text/markdown':
      return 'md';
    default:
      return '';
  }
};

export const generateNodeHash = () => {
  const HASH_LENGTH = 6;
  const HASH_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  const randomValues = new Uint8Array(HASH_LENGTH);
  crypto.getRandomValues(randomValues);
  return Array.from(randomValues, (value) => HASH_ALPHABET[value % HASH_ALPHABET.length]).join('');
};

export const getRangeSlice = <T extends DocumentWithId>(docs: T[], anchorId: string, targetId: string): T[] | null => {
  const ids = docs.map((d) => d.id);
  const anchorIndex = ids.indexOf(anchorId);
  const targetIndex = ids.indexOf(targetId);

  if (anchorIndex === -1 || targetIndex === -1) return null;

  const start = Math.min(anchorIndex, targetIndex);
  const end = Math.max(anchorIndex, targetIndex);
  return docs.slice(start, end + 1);
};

export const formatDuration = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const getDocumentIdFromPathname = (
  pathname: string,
  prefixes = [appRoutes.library, appRoutes.cortex]
): string | undefined => {
  for (const prefix of prefixes) {
    const pathPrefix = `${prefix}/`;
    if (!pathname.startsWith(pathPrefix)) continue;

    const routeId = pathname.slice(pathPrefix.length).split('/')[0];
    if (!routeId) return undefined;

    return getRealId(routeId);
  }

  return undefined;
};

export const normalizeNonBreakingSpace = (value: string) => {
  return value.replace(/>\s+&nbsp;/g, '>&nbsp;');
};
